import React from 'react';
import { Table, Select, Checkbox, Switch, Spin, Button, Space, Row, Col, Popconfirm, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import BasicTablePaging from 'components/BasicTablePaging/BasicTablePaging';
import { MIN_COLUMN_WIDTH, BIGGER_COLUMN_WIDTH, VERY_BIG_COLUMN_WIDTH } from 'constants/app';
import { FILTER_BUTTONS, PAYMENT_MODES, getDerivedParentServiceType } from 'constants/StationServicesConstant';
import './ServiceSettingStyle.scss';

const { Text } = Typography;

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('vi-VN')}đ`;

const ServiceSettingTable = ({
  filteredServices,
  listViewServices,
  selectedServiceTypes,
  dataFilter,
  activeFilter,
  updating,
  loading,
  handleChangePage,
  handleToggle,
  handleListToggle,
  onSaveListServices,
  isListSelectionChanged,
  onFilterChange,
  viewMode,
  onViewModeChange,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const getCategoryLabelByService = (service) => {
    const key = getDerivedParentServiceType(service);
    const matched = FILTER_BUTTONS.find((item) => item.key === key);
    return matched?.label || 'Tư vấn & dịch vụ khác';
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: MIN_COLUMN_WIDTH,
      align: 'center',
      render: (_, __, index) => dataFilter.skip + index + 1,
    },
    {
      title: 'Tên dịch vụ',
      dataIndex: 'serviceName',
      key: 'serviceName',
      width: VERY_BIG_COLUMN_WIDTH,
      ellipsis: true,
      render: (value) => <Text ellipsis={{ tooltip: value }}>{value}</Text>,
    },
    {
      title: 'Phân loại',
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: BIGGER_COLUMN_WIDTH,
      ellipsis: true,
      render: (_, record) => {
        const label = getCategoryLabelByService(record);
        return <Text ellipsis={{ tooltip: label }}>{label}</Text>;
      },
    },
    {
      title: 'Phí đặt lịch',
      key: 'bookingFee',
      width: 110,
      align: 'right',
      render: (_, record) => formatCurrency(record.serviceBookingFee ?? 20000),
    },
    {
      title: 'Giá tiền',
      dataIndex: 'servicePrice',
      key: 'servicePrice',
      width: 110,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Chấp nhận thanh toán online',
      dataIndex: 'enableOnlinePayment',
      key: 'enableOnlinePayment',
      align: 'center',
      className: 'service-payment-column',
      width: 170,
      render: (_, record) => (
        <Checkbox
          checked={record.enableOnlinePayment === 1}
          disabled={record.isActive !== 1 || !!updating[record.serviceType] || loading}
          onChange={(e) => handleToggle(record, PAYMENT_MODES.ONLINE.key, e.target.checked)}
        />
      ),
    },
    {
      title: 'Cho phép thu hộ',
      dataIndex: 'enablePaymentCollection',
      key: 'enablePaymentCollection',
      align: 'center',
      className: 'service-payment-column',
      width: 130,
      render: (_, record) => (
        <Checkbox
          checked={record.enablePaymentCollection === 1}
          disabled={record.isActive !== 1 || !!updating[record.serviceType] || loading}
          onChange={(e) => handleToggle(record, PAYMENT_MODES.COLLECTION.key, e.target.checked)}
        />
      ),
    },
    {
      title: 'Thu tiền trước',
      dataIndex: 'enablePrepay',
      key: 'enablePrepay',
      align: 'center',
      className: 'service-payment-column',
      width: 130,
      render: (_, record) => (
        <Checkbox
          checked={record.enablePrepay === 1}
          disabled={
            record.isActive !== 1 ||
            (record.enableOnlinePayment !== 1 && record.enablePrepay !== 1) ||
            !!updating[record.serviceType] ||
            loading
          }
          onChange={(e) => handleToggle(record, PAYMENT_MODES.PREPAY.key, e.target.checked)}
        />
      ),
    },
    {
      title: 'Hoạt động',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center',
      width: 90,
      render: (_, record) => (
        <Switch
          checked={record.isActive === 1}
          disabled={!!updating[record.serviceType] || loading}
          onChange={(checked) => handleToggle(record, 'isActive', checked)}
        />
      ),
    },
    {
      title: 'Hành động',
      key: 'action',
      width: BIGGER_COLUMN_WIDTH,
      align: 'center',
      render: (_, record) => (
        <Space size={4} className="service-setting-action">
          <Button
            type="link"
            size="small"
            className="service-setting-action__btn"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
            disabled={loading}
          />
          <Popconfirm
            title="Xóa dịch vụ này?"
            okText="Xóa"
            cancelText="Hủy"
            onConfirm={() => onDelete(record)}
          >
            <Button
              type="link"
              size="small"
              className="service-setting-action__btn"
              icon={<DeleteOutlined />}
              disabled={loading}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const groupedSections = FILTER_BUTTONS
    .filter((item) => item.key !== 'all')
    .map((item) => ({
      key: item.key,
      title: item.label,
      filterArray: item.filterArray,
    }));
  const filteredBySection = groupedSections
    .map((section) => ({
      ...section,
      services: (listViewServices || []).filter((service) => getDerivedParentServiceType(service) === section.key),
    }))
    .filter((section) => section.services.length > 0);

  const renderViewSwitch = () => (
    <div className="service-view-switch">
      <Button
        className="service-view-switch__btn"
        type={viewMode === 'table' ? 'primary' : 'default'}
        onClick={() => onViewModeChange('table')}
      >
        Bảng chi tiết
      </Button>
      <Button
        className="service-view-switch__btn"
        type={viewMode === 'list' ? 'primary' : 'default'}
        onClick={() => onViewModeChange('list')}
      >
        Danh sách
      </Button>
    </div>
  );

  const handleFilterChange = (value) => {
    onFilterChange(value || 'all');
  };

  return (
    <Spin spinning={loading}>
      <div className="service-setting-table">
        <div className="service-filter-section">
          <div className={`service-filter-section__row service-filter-section__row--${viewMode}`}>
            {viewMode === 'table' ? (
              <>
                <Space className="service-filter-section__actions">
                  <Select
                    value={activeFilter}
                    onChange={handleFilterChange}
                    style={{ width: 260 }}
                    placeholder="Chọn loại dịch vụ"
                    allowClear={activeFilter !== 'all'}
                  >
                    {FILTER_BUTTONS.map((btn) => (
                      <Select.Option key={btn.key} value={btn.key}>
                        {btn.label}
                      </Select.Option>
                    ))}
                  </Select>
                  <Button type="primary" onClick={onAdd} disabled={loading}>
                    Thêm mới
                  </Button>
                </Space>
                {renderViewSwitch()}
              </>
            ) : (
              <>
                <div className="service-filter-section__actions service-filter-section__actions--save">
                  <Button
                    type="primary"
                    onClick={onSaveListServices}
                    loading={loading}
                    disabled={!isListSelectionChanged}
                  >
                    Lưu lại
                  </Button>
                </div>
                {renderViewSwitch()}
              </>
            )}
          </div>
        </div>

        {viewMode === 'table' ? (
          <>
            <Table
              rowKey={(record) => record.stationServicesId || `meta-${record.serviceType}`}
              columns={columns}
              dataSource={filteredServices.slice(dataFilter.skip, dataFilter.skip + dataFilter.limit)}
              pagination={false}
              scroll={{ x: 1320 }}
              tableLayout="fixed"
            />
            <BasicTablePaging
              handlePaginations={handleChangePage}
              skip={dataFilter.skip}
              currentPage={Math.floor(dataFilter.skip / dataFilter.limit) + 1}
              count={filteredServices.slice(dataFilter.skip, dataFilter.skip + dataFilter.limit).length < dataFilter.limit}
            />
          </>
        ) : (
          <div className="service-list-view">
            <Row gutter={[24, 24]}>
              {filteredBySection.map((section) => (
                <Col key={section.key} xs={24} md={12} xl={8}>
                  <div className="service-list-section">
                    <div className="service-list-section__title">{section.title}</div>
                    {section.services.map((service) => (
                      <div key={service.stationServicesId || service.serviceType} className="service-list-item">
                        <Checkbox
                          checked={(selectedServiceTypes || []).includes(service.serviceType)}
                          disabled={!!updating[service.serviceType] || loading}
                          onChange={(e) => handleListToggle(service, e.target.checked)}
                        >
                          <Text ellipsis={{ tooltip: service.serviceName }}>{service.serviceName}</Text>
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default ServiceSettingTable;
